import React, { useState, useEffect } from 'react';
import { Form, FormGroup, Label, Input, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import api from '../../services/api';

const TeamPlayerForm = ({ registrationData, updateRegistrationData, checkingTeamName, teamNameAvailable, league }) => {
    const [teeChoices, setTeeChoices] = useState([]);
    const [defaultTee, setDefaultTee] = useState('');

    useEffect(() => {
        const fetchTeeChoices = async () => {
            if (league?.id) {
                try {
                    const [choicesResponse, leagueResponse] = await Promise.all([
                        api.get(`/league-tee-choices/?league=${league.id}`),
                        api.get(`/leagues/${league.id}/`)
                    ]);
                    const sorted = choicesResponse.data.sort((a, b) => a.sort - b.sort);
                    setTeeChoices(sorted);
                    const fetchedDefaultTee = leagueResponse.data.default_tee || '';
                    setDefaultTee(fetchedDefaultTee);
                } catch (error) {
                    console.error('Error fetching tee choices:', error);
                }
            }
        };
        fetchTeeChoices();
    }, [league?.id]);

    // Set default tee for players when defaultTee is loaded
    useEffect(() => {
        if (defaultTee) {
            const updates = {};
            ['player1', 'player2'].forEach(playerKey => {
                if (!registrationData[playerKey].tees) {
                    updates[playerKey] = {
                        ...registrationData[playerKey],
                        tees: defaultTee
                    };
                }
            });
            if (Object.keys(updates).length > 0) {
                updateRegistrationData(updates);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultTee]);

    const handleTeamNameChange = (e) => {
        updateRegistrationData({ teamName: e.target.value });
    };

    const handlePlayerChange = (playerNum, field, value) => {
        const playerKey = `player${playerNum}`;
        updateRegistrationData({
            [playerKey]: {
                ...registrationData[playerKey],
                [field]: value
            }
        });
    };

    const handleTeeCheckboxChange = (playerNum, teeValue, isChecked) => {
        const playerKey = `player${playerNum}`;
        const currentPlayer = registrationData[playerKey];
        
        // Get current checked tees (stored in a custom property for tracking)
        const checkedTees = currentPlayer.checkedTees || [];
        
        let updatedCheckedTees;
        if (isChecked) {
            updatedCheckedTees = [...checkedTees, teeValue];
        } else {
            updatedCheckedTees = checkedTees.filter(t => t !== teeValue);
        }
        
        // Determine the final tee value
        let finalTee;
        if (updatedCheckedTees.length === 0) {
            // No checkboxes checked, use default tee
            finalTee = defaultTee;
        } else {
            // Find the lowest sorted checked tee
            const checkedChoices = teeChoices.filter(choice => updatedCheckedTees.includes(choice.tee));
            checkedChoices.sort((a, b) => a.sort - b.sort);
            finalTee = checkedChoices[0]?.tee || defaultTee;
        }
        console.log(`Player ${playerNum} final tee set to:`, finalTee);
        updateRegistrationData({
            [playerKey]: {
                ...currentPlayer,
                checkedTees: updatedCheckedTees,
                tees: finalTee
            }
        });
    };

    const isTeechecked = (playerNum, teeValue) => {
        const playerKey = `player${playerNum}`;
        const currentPlayer = registrationData[playerKey];
        const checkedTees = currentPlayer.checkedTees || [];
        return checkedTees.includes(teeValue);
    };

    return (
        <Form>
            {/* Team Name */}
            <Card className="mb-4">
                <CardBody>
                    <FormGroup>
                        <Label for="teamName" className="fw-bold">
                            Team Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="teamName"
                            name="teamName"
                            value={registrationData.teamName}
                            onChange={handleTeamNameChange}
                            placeholder="Enter your team name (min 3 characters)"
                            required
                            valid={teamNameAvailable === true && registrationData.teamName.trim().length >= 3}
                            invalid={teamNameAvailable === false && registrationData.teamName.trim().length >= 3}
                        />
                        {checkingTeamName && (
                            <small className="text-muted">
                                <span className="spinner-border spinner-border-sm me-1"></span>
                                Checking availability...
                            </small>
                        )}
                        {!checkingTeamName && teamNameAvailable === true && registrationData.teamName.trim().length >= 3 && (
                            <small className="text-success">
                                <i className="fas fa-check-circle me-1"></i>
                                Team name is available
                            </small>
                        )}
                        {!checkingTeamName && teamNameAvailable === false && (
                            <small className="text-danger d-block mt-1">
                                This team name is already taken
                            </small>
                        )}
                        {registrationData.teamName.trim().length > 0 && registrationData.teamName.trim().length < 3 && (
                            <small className="text-muted d-block mt-1">
                                Team name must be at least 3 characters
                            </small>
                        )}
                    </FormGroup>
                </CardBody>
            </Card>

            {/* Player 1 (Required) */}
            <Card className="mb-4">
                <CardHeader className="bg-success text-white">
                    <h5 className="mb-0">
                        Player 1 <span className="badge bg-light text-success ms-2">Required</span>
                    </h5>
                </CardHeader>
                <CardBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="player1FirstName">
                                    First Name <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="player1FirstName"
                                    value={registrationData.player1.first_name}
                                    onChange={(e) => handlePlayerChange(1, 'first_name', e.target.value)}
                                    placeholder="First name"
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="player1LastName">
                                    Last Name <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    id="player1LastName"
                                    value={registrationData.player1.last_name}
                                    onChange={(e) => handlePlayerChange(1, 'last_name', e.target.value)}
                                    placeholder="Last name"
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="player1Email">
                                    Email <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="email"
                                    id="player1Email"
                                    value={registrationData.player1.email}
                                    onChange={(e) => handlePlayerChange(1, 'email', e.target.value)}
                                    placeholder="email@example.com"
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="player1Phone">
                                    Phone Number <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="tel"
                                    id="player1Phone"
                                    value={registrationData.player1.phone_number}
                                    onChange={(e) => handlePlayerChange(1, 'phone_number', e.target.value)}
                                    placeholder="(555) 555-5555"
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {teeChoices.length > 0 && (
                        <Row>
                            <Col md={12}>
                                <FormGroup tag="fieldset">
                                    <Label className="fw-bold">
                                        Tees
                                    </Label>
                                    <div className="d-flex gap-4 mt-2">
                                        {teeChoices.map((choice) => (
                                            <FormGroup check key={choice.id}>
                                                <Input
                                                    type="checkbox"
                                                    id={`player1Tee${choice.id}`}
                                                    checked={isTeechecked(1, choice.tee)}
                                                    onChange={(e) => handleTeeCheckboxChange(1, choice.tee, e.target.checked)}
                                                />
                                                <Label check for={`player1Tee${choice.id}`}>
                                                    {choice.prompt}
                                                </Label>
                                            </FormGroup>
                                        ))}
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                </CardBody>
            </Card>

            {/* Player 2 (Optional) */}
            <Card className="mb-4">
                <CardHeader className="bg-secondary text-white">
                    <h5 className="mb-0">
                        Player 2 <span className="badge bg-light text-secondary ms-2">Optional</span>
                    </h5>
                </CardHeader>
                <CardBody>
                    <p className="text-muted small mb-3">
                        <i className="fas fa-info-circle me-1"></i>
                        All fields are optional for the second player. You can add this information now or leave it blank.
                    </p>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="player2FirstName">First Name</Label>
                                <Input
                                    type="text"
                                    id="player2FirstName"
                                    value={registrationData.player2.first_name}
                                    onChange={(e) => handlePlayerChange(2, 'first_name', e.target.value)}
                                    placeholder="First name"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="player2LastName">Last Name</Label>
                                <Input
                                    type="text"
                                    id="player2LastName"
                                    value={registrationData.player2.last_name}
                                    onChange={(e) => handlePlayerChange(2, 'last_name', e.target.value)}
                                    placeholder="Last name"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="player2Email">Email</Label>
                                <Input
                                    type="email"
                                    id="player2Email"
                                    value={registrationData.player2.email}
                                    onChange={(e) => handlePlayerChange(2, 'email', e.target.value)}
                                    placeholder="email@example.com"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="player2Phone">Phone Number</Label>
                                <Input
                                    type="tel"
                                    id="player2Phone"
                                    value={registrationData.player2.phone_number}
                                    onChange={(e) => handlePlayerChange(2, 'phone_number', e.target.value)}
                                    placeholder="(555) 555-5555"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {teeChoices.length > 0 && (
                        <Row>
                            <Col md={12}>
                                <FormGroup tag="fieldset">
                                    <Label className="fw-bold">
                                        Tees
                                    </Label>
                                    <div className="d-flex gap-4 mt-2">
                                        {teeChoices.map((choice) => (
                                            <FormGroup check key={choice.id}>
                                                <Input
                                                    type="checkbox"
                                                    id={`player2Tee${choice.id}`}
                                                    checked={isTeechecked(2, choice.tee)}
                                                    onChange={(e) => handleTeeCheckboxChange(2, choice.tee, e.target.checked)}
                                                />
                                                <Label check for={`player2Tee${choice.id}`}>
                                                    {choice.prompt}
                                                </Label>
                                            </FormGroup>
                                        ))}
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                </CardBody>
            </Card>

            {/* Notes Field */}
            <Card className="mb-4">
                <CardHeader className="bg-light">
                    <h5 className="mb-0">Additional Notes</h5>
                </CardHeader>
                <CardBody>
                    <FormGroup>
                        <Label for="notes">Notes (Optional)</Label>
                        <Input
                            type="textarea"
                            id="notes"
                            name="notes"
                            value={registrationData.notes}
                            onChange={(e) => updateRegistrationData({ notes: e.target.value })}
                            placeholder={league?.notes_text || "Any additional information or special requests..."}
                            rows="4"
                        />
                    </FormGroup>
                </CardBody>
            </Card>

            <div className="text-muted small">
                <i className="fas fa-asterisk text-danger me-1" style={{ fontSize: '0.5rem' }}></i>
                Required fields
            </div>
        </Form>
    );
};

export default TeamPlayerForm;
